import React, { useId, useState } from "react";
import { Link } from "react-router-dom";
import Icon from "./Icon";

// Remember which windows someone rolled up, per device. Counts like
// "received (3)" are ignored so the window keeps its state as mail arrives.
const storageKey = (title: string) =>
  `penpal-min:${title.replace(/\s*\(\d+\)\s*$/, "").trim()}`;

function loadMinimized(title: string) {
  try {
    return localStorage.getItem(storageKey(title)) === "1";
  } catch {
    return false;
  }
}

function saveMinimized(title: string, min: boolean) {
  try {
    if (min) localStorage.setItem(storageKey(title), "1");
    else localStorage.removeItem(storageKey(title));
  } catch {
    // storage unavailable; it just won't be remembered
  }
}

// A cream "letter paper" panel with an early-2000s window title bar.
// _ rolls the window up to its title bar, □ rolls it back down. Forms
// and dialogs pass minimizable={false} so they can't be hidden mid-task.
export default function Window({
  title,
  tone = "pink",
  className = "",
  bodyClassName = "",
  minimizable = true,
  children,
}: {
  title: string;
  tone?: "pink" | "blue" | "tan" | "lavender" | "mint";
  className?: string;
  bodyClassName?: string;
  minimizable?: boolean;
  children: React.ReactNode;
}) {
  const bodyId = useId();
  const [minimized, setMinimized] = useState(() => minimizable && loadMinimized(title));

  const setMin = (min: boolean) => {
    setMinimized(min);
    saveMinimized(title, min);
  };

  return (
    <section className={`window ${tone} ${minimized ? "is-minimized" : ""} ${className}`}>
      <div
        className={`window-title${minimizable ? " can-minimize" : ""}`}
        onClick={minimizable && minimized ? () => setMin(false) : undefined}
        onDoubleClick={minimizable ? () => setMin(!minimized) : undefined}
      >
        <span className="window-title-text">{title}</span>
        {minimizable ? (
          <span className="window-controls">
            <button
              type="button"
              aria-label={`Minimize ${title}`}
              aria-expanded={!minimized}
              aria-controls={bodyId}
              disabled={minimized}
              onClick={(e) => {
                e.stopPropagation();
                setMin(true);
              }}
            >
              _
            </button>
            <button
              type="button"
              aria-label={`Restore ${title}`}
              aria-expanded={!minimized}
              aria-controls={bodyId}
              disabled={!minimized}
              onClick={(e) => {
                e.stopPropagation();
                setMin(false);
              }}
            >
              □
            </button>
            <span aria-hidden="true">×</span>
          </span>
        ) : (
          <span className="window-controls" aria-hidden="true">
            <span>_</span>
            <span>□</span>
            <span>×</span>
          </span>
        )}
      </div>
      <div id={bodyId} className={`window-body ${bodyClassName}`} hidden={minimized}>
        {children}
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="footer">
      <div className="badges" aria-hidden="true">
        <span className="badge88 red">TRANS RIGHTS!</span>
        <span className="badge88 orange">WRITE MORE LETTERS</span>
        <span className="badge88 yellow">BE GAY DO KINDNESS</span>
        <span className="badge88 green">NO TERFS</span>
        <span className="badge88 blue">NO RACISM</span>
      </div>
      <p>
        made with <Icon name="heart" tone="pink" size={14} label="love" /> by
        trans folks ✿ best viewed
        with a cup of tea <Icon name="tea" tone="ink" size={14} /> ·{" "}
        <Link to="/who-we-are">Who we are</Link> · <Link to="/care">Care</Link> ·{" "}
        <Link to="/guidelines">Guidelines</Link> · <Link to="/privacy">Privacy</Link>
      </p>
    </footer>
  );
}
